import { useEffect, useRef } from 'react';
import { analyseRecordSensitivity } from './supportResources';
import { RedactionAwareText } from './RedactionAwareText';
import { SaveToCollection } from '../notes/SaveToCollection';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/**
 * "14 MAR 2011" / "MAR 2011" / "2011" from a YYYY[-MM[-DD]] string.
 * Falls back to the server's human date when the raw date is missing.
 */
export function eyebrowDate(date, dateHuman) {
  if (!date) return dateHuman ? String(dateHuman).toUpperCase() : 'Undated';
  const [y, m, d] = String(date).split('-');
  const month = m ? MONTHS[parseInt(m, 10) - 1] : null;
  const parts = [];
  if (d && month) parts.push(String(parseInt(d, 10)));
  if (month) parts.push(month);
  if (y) parts.push(y);
  return parts.join(' ') || 'Undated';
}

/**
 * "5 MAR" / "MAR" for a record sitting under its own year on the timeline
 * spine; the year is only repeated when the record's date is in another
 * year (an undated or unlocking group) or there is no date.
 */
export function dateWithinYear(date, dateHuman, groupYear) {
  const full = eyebrowDate(date, dateHuman);
  if (!date || !groupYear || !String(date).startsWith(String(groupYear))) return full;
  return full.replace(new RegExp(`\\s*${groupYear}$`), '') || full;
}

// Document types whose records are, on their own, a routine entry: a visit,
// a note, a call, a letter. They read as one line on the timeline spine
// unless something about the record itself makes it matter (below).
const LINE_TYPES = /visit|note|contact|correspondence|letter|email|phone|call|log|diary|chronology|health visitor|record sheet|running record|milestone/i;

/**
 * How much room a record takes on the timeline spine: 'card' for something
 * that matters (a review, plan, conference, report or order; anything
 * sensitive; whole pages blacked out; gaps filled from another copy), 'line'
 * for a routine entry. A line can still carry a small flag word.
 */
export function recordWeight(record) {
  if (!record) return 'card';
  if (record.sensitive) return 'card';
  if ((record.redacted_pages || 0) > 0) return 'card';
  if ((record.radar_summary?.resolved || 0) > 0) return 'card';
  if ((record.source_count || 0) > 1) return 'card';
  const type = record.doc_type || '';
  if (LINE_TYPES.test(type)) return 'line';
  return 'card';
}

const Chip = ({ children, tone = 'neutral', title, testId }) => (
  <span
    title={title}
    data-testid={testId}
    className={`inline-flex items-center rounded-full px-[11px] py-1 text-[13px] font-medium leading-tight ${
      tone === 'brand' ? 'bg-brand-wash text-brand-ink' : tone === 'needs' ? 'bg-amber-wash text-amber-ink' : 'bg-surface text-ink-70'
    }`}
  >
    {children}
  </span>
);

/**
 * Placeholder shown while a record is still being decrypted. Everything
 * that was never encrypted is real (year, document type, organisations,
 * redaction flag); the title is a blurred stand-in the same height as a
 * real one, so the timeline doesn't jump when the text arrives.
 */
const PendingRecordCard = ({ record, onVisible, compact = false }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!onVisible || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    const el = ref.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onVisible(record.id);
    }, { rootMargin: '600px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible, record.id]);

  const docType = record.doc_type || 'Record';
  const orgNames = record.organisation_names || [];
  const { sensitive } = analyseRecordSensitivity(record);

  return (
    <div
      ref={ref}
      aria-busy="true"
      className={`w-full text-left rounded-lg ${compact ? 'px-4 py-3' : 'p-5 sm:px-6 sm:py-5'} ${sensitive ? 'bg-care-wash' : 'bg-ground'}`}
      data-testid={`record-card-pending-${record.id}`}
    >
      <p className="pd-mono text-ink-55">
        {record.year || 'Unlocking'}
        <span aria-hidden="true"> · </span>
        {docType}
      </p>
      <span className="sr-only">Unlocking this record</span>
      {/* Greeked title: same type size as the real one, blurred and
          unselectable. Width varies so a column of them doesn't look like
          a table. */}
      <p
        aria-hidden="true"
        className={`font-heading ${compact ? 'text-[19px] mt-1' : 'text-[19px] sm:text-[22px] mt-2'} font-semibold tracking-[-0.02em] leading-[1.25] text-ink-55 blur-[5px] select-none truncate motion-safe:animate-pulse`}
        style={{ maxWidth: `${55 + ((record.id?.charCodeAt(0) || 0) % 35)}%` }}
      >
        Record still locked, decrypting now
      </p>
      {(orgNames.length > 0 || record.has_redactions) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {orgNames.slice(0, 2).map((name, i) => (
            <Chip key={`${record.id}-org-${i}`}>{name}</Chip>
          ))}
          {orgNames.length > 2 && <Chip>+{orgNames.length - 2} more</Chip>}
          {record.has_redactions && <Chip>Redactions</Chip>}
        </div>
      )}
    </div>
  );
};

/**
 * Record card for My Life. Purely display — no AI summary, no excerpt.
 * Order: mono eyebrow (date · document type) → server-assembled title → chips.
 * Sensitive records sit on care-wash and add one plain sentence.
 *
 * `record.pending` (from GET /records/index) renders the placeholder above
 * and reports itself via `onVisible` so the page can decrypt what's on
 * screen first.
 */
export const RecordCard = ({ record, onClick, onVisible, compact = false, groupYear = null }) => {
  if (record.pending) return <PendingRecordCard record={record} onVisible={onVisible} compact={compact} />;
  return <DecryptedRecordCard record={record} onClick={onClick} compact={compact} groupYear={groupYear} />;
};

/**
 * One line on the timeline spine for a routine record: date, title, its
 * kind, at most a flag word or two, and "View". Pending records are a
 * greeked line of the same height. No Save here — open it to save it.
 */
export const RecordLine = ({ record, onClick, onVisible, groupYear = null, align = 'left' }) => {
  const ref = useRef(null);
  const pending = !!record.pending;
  useEffect(() => {
    if (!pending || !onVisible || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    const el = ref.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onVisible(record.id);
    }, { rootMargin: '600px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [pending, onVisible, record.id]);

  const docType = record.doc_type || 'Record';
  const right = align === 'right';
  const flags = [];
  if (record.has_redactions) flags.push('Redactions');
  if (record.illegible_count >= 5) flags.push('Some writing couldn’t be read');
  if (record.low_confidence) flags.push('Check the details');

  if (pending) {
    return (
      <div
        ref={ref}
        aria-busy="true"
        className={`flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 py-1 w-full ${right ? 'sm:justify-end sm:text-right' : ''}`}
        data-testid={`record-card-pending-${record.id}`}
      >
        <span className="pd-mono text-ink-55">{record.year || 'Unlocking'}</span>
        <span className="sr-only">Unlocking this record</span>
        <span
          aria-hidden="true"
          className="text-[16px] text-ink-55 blur-[4px] select-none truncate motion-safe:animate-pulse"
          style={{ maxWidth: `${40 + ((record.id?.charCodeAt(0) || 0) % 30)}%` }}
        >
          Record still locked, decrypting now
        </span>
        <span className="text-sm text-ink-55">· {docType}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 py-1 w-full text-left rounded-md transition-colors duration-state hover:bg-ground ${right ? 'sm:justify-end sm:text-right' : ''}`}
      data-testid={`record-card-${record.id}`}
      data-weight="line"
    >
      <span className="pd-mono text-ink-55 whitespace-nowrap" data-testid={`record-date-${record.id}`}>
        {record.date_approximate && <span className="normal-case">c. </span>}
        {dateWithinYear(record.date, record.date_human, groupYear)}
      </span>
      <span className="text-[16px] text-ink min-w-0 break-words">
        <RedactionAwareText text={record.assembled_label} />
        <span className="text-sm text-ink-55"> · {docType}</span>
      </span>
      {flags.map((f) => (
        <span key={f} className="text-[13px] text-ink-55">{f}</span>
      ))}
      <span className="text-sm text-brand border-b border-brand/60 group-hover:border-brand whitespace-nowrap">View</span>
    </button>
  );
};

const DecryptedRecordCard = ({ record, onClick, compact = false, groupYear = null }) => {
  const { sensitive, label: sensitiveLabel } = analyseRecordSensitivity(record);
  const dateText = compact ? dateWithinYear(record.date, record.date_human, groupYear) : eyebrowDate(record.date, record.date_human);
  const dateApprox = record.date_approximate;
  const docType = record.doc_type || 'Record';
  const orgNames = record.organisation_names || [];
  const gapsFilled = record.radar_summary?.resolved || 0;

  return (
    <div className="relative">
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg ${compact ? 'px-4 py-3 pr-20' : 'p-5 pr-24 sm:px-6 sm:pr-28 sm:py-5'} transition-colors duration-state ${
        sensitive ? 'bg-care-wash hover:bg-[#F8DCCB]' : 'bg-ground hover:bg-inset'
      }`}
      data-testid={`record-card-${record.id}`}
      data-weight="card"
    >
      <p className="pd-mono text-ink-55" data-testid={`record-date-${record.id}`}>
        {dateApprox && <span className="normal-case">c. </span>}
        {dateText}
        <span aria-hidden="true"> · </span>
        {docType}
      </p>

      <h3 className={`font-heading ${compact ? 'text-[19px] mt-1' : 'text-[19px] sm:text-[22px] mt-2'} font-semibold tracking-[-0.02em] leading-[1.25] text-ink break-words`}>
        <RedactionAwareText text={record.assembled_label} />
      </h3>

      {sensitive && (
        <p className={`${compact ? 'text-[15px] mt-1' : 'text-[16px] mt-2'} text-care-ink leading-relaxed`}>
          Mentions {(sensitiveLabel || 'sensitive content').toLowerCase()}. Open it when you're ready.
        </p>
      )}

      {(orgNames.length > 0 || record.source_count > 1 || gapsFilled > 0 || record.has_redactions || record.low_confidence) && (
        <div className={`flex flex-wrap gap-2 ${compact ? 'mt-2' : 'mt-3'}`}>
          {orgNames.slice(0, 2).map((name, i) => (
            <Chip key={`${record.id}-org-${i}`}>{name}</Chip>
          ))}
          {orgNames.length > 2 && (
            <Chip title={orgNames.slice(2).join(', ')}>+{orgNames.length - 2} more</Chip>
          )}
          {record.source_count > 1 && (
            <Chip
              title="More than one organisation provided a copy of this document"
              testId={`record-sources-${record.id}`}
            >
              {record.source_count} sources
            </Chip>
          )}
          {gapsFilled > 0 && (
            <Chip tone="brand" title="Blacked-out text recovered from another organisation's copy">
              {gapsFilled} gap{gapsFilled === 1 ? '' : 's'} filled
            </Chip>
          )}
          {record.redacted_pages > 0 && (
            <Chip
              tone="needs"
              title="Whole pages of this record were blacked out before it was sent"
              testId={`record-redacted-pages-${record.id}`}
            >
              {record.redacted_pages} whole page{record.redacted_pages === 1 ? '' : 's'} blacked out
            </Chip>
          )}
          {record.has_redactions && (
            <Chip
              title="At least one copy has text blacked out"
              testId={`record-redactions-${record.id}`}
            >
              {record.radar_summary?.total_redactions
                ? `${record.radar_summary.total_redactions} redaction${record.radar_summary.total_redactions === 1 ? '' : 's'}`
                : 'Redactions'}
            </Chip>
          )}
          {record.illegible_count >= 5 && (
            <Chip
              title="Some of the handwriting or scan couldn't be read — these parts show as 'unreadable', not blacked out"
              testId={`record-illegible-${record.id}`}
            >
              Some writing couldn’t be read
            </Chip>
          )}
          {record.low_confidence && (
            <Chip
              title="The details on this card were read with low confidence. Open it to check."
              testId={`record-low-confidence-${record.id}`}
            >
              Check the details
            </Chip>
          )}
        </div>
      )}
    </button>
    {/* Save while browsing — beside the card, never inside its button. */}
    <div className={`absolute ${compact ? 'top-2 right-2' : 'top-3 right-3'}`}>
      <SaveToCollection targetType="record" targetId={record.id} label="Save" variant="ghost" testId={`record-card-save-${record.id}`} />
    </div>
    </div>
  );
};

export default RecordCard;
